import {
  BoxProps,
  IconButton,
  Typography,
  GridLegacy,
  Divider,
  Stack,
  Box,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ALL_RECORD_TYPE_IDS, ALL_RECORD_TYPES } from 'constants/recordTypes';
import { FieldValues, useFieldArray, useForm } from 'react-hook-form';
import { GenericResource } from 'types/api';
import { StockDocType, StockDocTypeLabels } from 'types/inventory';
import { FormValues } from 'ui-component/UploadStockDocsWidget';
import _ from 'lodash';
import FileUploadInput from 'ui-component/FileUploadInput';
import WithSkeleton from 'ui-component/extended/WithSkeleton';
import { useState } from 'react';
import { ExtendedDialog } from 'ui-component/extended/ExtendedDialog';
import { IconX } from '@tabler/icons-react';
import { actionButtonSpacing } from 'constants/themeConstants';
import useSnackbar from 'hooks/useSnackbar';
import { useCreateStockDocsV2 } from 'hooks/useCreateStockDocsV2';
import { useCreateStockDocumentRelatedObjectMutation } from 'store/slices/clientV2/stockDocuments';
import {
  StockDocumentRelatedObjectPOSTSchema,
  StockDocumentSchema,
} from 'types/clientV2/stockDocuments';
import { OrgLite } from 'types/org';

export interface UnsavedStockDocument {
  id?: string;
  name: string;
  file: File;
  documentType: StockDocType;
  notes: string;
  uploading?: boolean;
}

// Extended form values for this component
interface ExtendedFormValues extends FormValues {
  stockDocuments: (FormValues['stockDocuments'][0] & {
    id?: string;
    name?: string;
    uploading?: boolean;
  })[];
}

interface InlineUploadDocumentsDropZoneProps<T extends GenericResource> {
  record?: T;
  recordType: ALL_RECORD_TYPE_IDS;
  setSelectedDocumentId?: (id: string) => void;
  onSave?: (ids: StockDocumentSchema['id'][]) => void;
  boxSx?: BoxProps['sx'];
  isLoading?: boolean;
  recordOrgId?: string | null;
  size?: 'sm' | 'md';
  onDialogCloseWithoutSave?: (docs: UnsavedStockDocument[]) => void;
}

const InlineUploadDocumentsDropZone = <T extends GenericResource>({
  record,
  recordType,
  recordOrgId,
  setSelectedDocumentId,
  onSave,
  boxSx,
  isLoading,
  size = 'sm',
  onDialogCloseWithoutSave,
}: InlineUploadDocumentsDropZoneProps<T>) => {
  const theme = useTheme();
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);

  const recordTypeDefinition = ALL_RECORD_TYPES[recordType];
  const { isLoading: isLoadingCreateStockDoc, createStockDocuments } =
    useCreateStockDocsV2();

  const [
    createStockDocumentRelatedObject,
    { isLoading: isLoadingCreateStockDocumentRelatedObject },
  ] = useCreateStockDocumentRelatedObjectMutation();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm<ExtendedFormValues>({ defaultValues: { stockDocuments: [] } });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'stockDocuments',
  });

  const stockDocuments = watch('stockDocuments');

  const handleFileUpload = async (newFiles?: UnsavedStockDocument[]) => {
    const results = await createStockDocuments(
      _.map(newFiles, (i) => ({
        file: i.file,
        documentType: StockDocType.FILE,
        nonBillable: true,
        notes: '',
        filename: i.file.name,
        processDocument: false,
        autoClassify: true,
      }))
    );

    const resultValues = _.map(results, 'value');

    const currentValues = getValues('stockDocuments') as UnsavedStockDocument[];

    const newValues = _.map(currentValues, (cv) => ({
      ...cv,
      id: resultValues.find((rv) => rv.name === cv.file.name)?.id || cv.id,
      name:
        resultValues.find((rv) => rv.name === cv.file.name)?.name || cv.name,
      documentType:
        resultValues.find((rv) => rv.name === cv.file.name)?.document_type ||
        cv.documentType,
      notes:
        resultValues.find((rv) => rv.name === cv.file.name)?.notes || cv.notes,
      uploading: false,
    }));

    setValue('stockDocuments', newValues);
  };

  const onBatchChange = (newFiles?: File[]) => {
    if (newFiles) {
      const newValues = _.map(newFiles, (f) => ({
        id: _.toString(crypto.randomUUID()),
        file: f,
        documentType: StockDocType.FILE,
        notes: '',
        name: f.name,
        uploading: true,
      }));
      append(newValues);
      handleFileUpload(newValues);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const onSubmit = async (data: FieldValues) => {
    try {
      if (onDialogCloseWithoutSave) {
        onDialogCloseWithoutSave(
          _.map(
            data.stockDocuments,
            (d) =>
              ({
                id: d.id,
                name: d.name,
                file: d.file,
                documentType: d.documentType,
                notes: d.notes,
              } as UnsavedStockDocument)
          )
        );
        handleClose();
        return;
      }
      // then we create the joins
      const orgId =
        recordOrgId ?? _.get(record, 'org.id') ?? _.get(record, 'org');

      const stockDocIds: string[] = _.map(data.stockDocuments, 'id');

      const joins: StockDocumentRelatedObjectPOSTSchema[] = _.map(
        stockDocIds,
        (id) => ({
          stockDocument: id,
          relatedObjectId: record?.id ?? '',
          relatedObjectType: recordTypeDefinition.flagshipModel,
          org: orgId as OrgLite['id'],
        })
      );

      await createStockDocumentRelatedObject(joins).unwrap();

      dispatchSuccessSnackbar('Documents created');

      if (setSelectedDocumentId) {
        setSelectedDocumentId(stockDocIds[0]);
      }

      if (onSave) {
        onSave(stockDocIds);
      }

      handleClose();
    } catch (err) {
      dispatchErrorSnackbar('Request failed');
      throw err;
    }
  };

  return (
    <>
      <Box sx={boxSx}>
        <WithSkeleton isLoading={isLoading}>
          <FileUploadInput
            batchFileMode
            onBatchChange={onBatchChange}
            allowAllExtensions
            defaultText="Upload Files"
            inlineVariant
            inlineVariantHeight={size}
          />
        </WithSkeleton>
      </Box>
      <ExtendedDialog
        open={open}
        title="Upload Documents"
        onCloseDialog={handleClose}
        submitButtonCopy="Save"
        isSubmitting={
          isLoadingCreateStockDoc || isLoadingCreateStockDocumentRelatedObject
        }
        submitDisabled={(stockDocuments || []).length < 1}
        confirmClose
        isForm
        onSubmit={handleSubmit(onSubmit)}
        maxWidth="md"
        additionalContentAboveForm={
          <FileUploadInput
            batchFileMode
            onBatchChange={onBatchChange}
            allowAllExtensions
            defaultText="Upload Files"
            inlineVariant
            inlineVariantHeight="md"
          />
        }
      >
        <Stack
          direction="column"
          justifyContent="flex-start"
          alignItems="stretch"
          spacing={actionButtonSpacing}
          divider={<Divider flexItem />}
        >
          {fields.map((field, index) => (
            <Stack
              key={field.id}
              direction="column"
              justifyContent="flex-start"
              alignItems="stretch"
            >
              <GridLegacy container alignItems="center">
                <GridLegacy item xs>
                  <Typography variant="h5">{field.file.name}</Typography>
                </GridLegacy>
                {field.uploading ? (
                  <GridLegacy item xs>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                      }}
                    >
                      <CircularProgress size={20} />
                      <Typography variant="h5">Uploading...</Typography>
                    </Stack>
                  </GridLegacy>
                ) : (
                  <GridLegacy item xs>
                    <Typography>
                      {StockDocTypeLabels[field.documentType]}
                    </Typography>
                  </GridLegacy>
                )}
                <GridLegacy item xs="auto">
                  <IconButton
                    aria-label="delete"
                    onClick={() => remove(index)}
                    disabled={field.uploading}
                  >
                    <IconX color={theme.palette.error.main} />
                  </IconButton>
                </GridLegacy>
              </GridLegacy>
              {!field.uploading && (
                <Typography variant="caption">{field.notes}</Typography>
              )}
            </Stack>
          ))}
        </Stack>
      </ExtendedDialog>
    </>
  );
};

export default InlineUploadDocumentsDropZone;
